import { Schema } from "effect";
import { mature as isMature } from "../../srs/fsrs.ts";
import { Instant, NonNegativeInt, PositiveInt } from "../../srs/types.ts";
import type { SetStatus } from "../api.ts";
import { byOrder, isDue, isNew, isScheduled, newCardAvailable, studied } from "../model/queue.ts";
import { SetId, type Flashcard, type FlashcardSet, type Prerequisite } from "../model/types.ts";

export const SetSummary = Schema.Struct({
  id: SetId,
  slug: Schema.String,
  name: Schema.String,
  description: Schema.NullOr(Schema.String),
  level: PositiveInt,
  levels: PositiveInt,
  due: NonNegativeInt,
  lessons: NonNegativeInt,
  learning: NonNegativeInt,
  mature: NonNegativeInt,
  locked: NonNegativeInt,
  total: NonNegativeInt,
  queued: NonNegativeInt,
  nextDueAt: Schema.NullOr(Instant),
  // Each kind the set's settings declare, and whether it is paused.
  kinds: Schema.Record(Schema.String, Schema.Boolean),
});
export type SetSummary = typeof SetSummary.Type;

export function setStatus(set: FlashcardSet, cards: readonly Flashcard[], now: Instant): SetStatus {
  const scheduled = cards.filter(studied(set)).filter(isScheduled);
  return {
    due: NonNegativeInt.make(scheduled.filter((c) => isDue(c, now)).length),
    nextDueAt: scheduled.reduce<Instant | null>((min, c) => (min === null || c.state.dueAt < min ? c.state.dueAt : min), null),
  };
}

export function summarise(
  set: FlashcardSet,
  cards: readonly Flashcard[],
  now: Instant,
  prerequisites: readonly Prerequisite[] = [],
): SetSummary {
  // TODO: Aggregate counts/extrema once.
  const mine = cards.filter(studied(set));
  const available = newCardAvailable(mine, prerequisites);
  const fresh = mine.filter(isNew);
  const lessons = fresh.filter(available).sort(byOrder);
  const scheduled = mine.filter(isScheduled);
  const { due, nextDueAt } = setStatus(set, mine, now);
  const mature = scheduled.filter((c) => isMature(c.state)).length;
  const levels = PositiveInt.make(mine.reduce((max, c) => Math.max(max, c.level), 1));
  const count = (n: number) => NonNegativeInt.make(n);
  return {
    id: set.id,
    slug: set.slug,
    name: set.name,
    description: set.description,
    level: lessons[0]?.level ?? levels,
    levels,
    due,
    lessons: count(lessons.length),
    learning: count(scheduled.length - mature),
    mature: count(mature),
    locked: count(fresh.length - lessons.length),
    total: count(mine.length),
    queued: count(due + Math.min(lessons.length, set.settings.lessonBatchSize)),
    nextDueAt,
    kinds: Object.fromEntries(Object.entries(set.settings.kinds).map(([k, s]) => [k, s.paused === true])),
  };
}
